// The BYOC kinds as the console reads them (resources.md Pool, Node, EnrollmentToken, PoolAction, CloudAccount;
// ADR-117), with the pure helpers the Infrastructure pages share. Field names follow api/nodus/v1.
import type { Condition, KObject } from "@nodus/api";
import type { CloudSpendReport } from "./cloud-spend";

export type RoutingMode = "Prefer" | "Only";
export type WaitPolicy = "Never" | "Cheaper" | "Timeout";
export type BurstPolicy = "Allow" | "Approve" | "Deny";
export type ActMode = "Off" | "Shadow" | "Propose" | "Auto";

export type PoolSpec = {
  routing?: { mode?: RoutingMode; waitPolicy?: WaitPolicy; waitTimeout?: string; burstToMarket?: BurstPolicy };
  sandboxCapable?: boolean;
  predict?: { enabled?: boolean; horizon?: string };
  act?: { mode?: ActMode; paused?: boolean; policies?: ActPolicy[] };
}

export type ActPolicy = {
  action: "IdleReclaim" | "Drain" | "Defragment" | "WaitTuning" | "Burst";
  idleFor?: string;
  schedule?: string;
  duration?: string;
  minWaitTimeout?: string;
  maxWaitTimeout?: string;
  maxGPUs?: number;
  maxRateUSDPerHour?: string;
}

export type PoolStatus = {
  phase?: string;
  conditions?: Condition[];
  capacity?: { nodes: number; readyNodes: number; gpus?: Record<string, { total: number; free: number }> };
  utilization?: { window?: string; gpuPercent: number; deviceHours?: string };
  predict?: { enabledTime: string };
  forecast?: { generatedTime: string; horizon: string; peakDemandGPUs: { p50: number; p90: number }; shortfallTime?: string };
  routing?: { waiting: number; burstPending: number };
  act?: { pending: number; lastActionTime?: string };
}

export type Pool = KObject<PoolSpec, PoolStatus & Record<string, unknown>>;

export type NodeStatus = {
  pool: string;
  poolProject?: string;
  conditions?: Condition[];
  inventory?: { cpu: number; memoryMiB: number; diskGiB: number; gpus?: { type: string; memoryMiB: number }[]; os?: string; nodusdVersion?: string };
  allocatable?: { cpu: number; memoryMiB: number; gpus?: Record<string, number> };
  utilization?: { gpuPercent: number; memoryPercent: number };
  running: number;
  lastHeartbeatTime?: string;
  cloudAccount?: string;
  drainedTime?: string;
}

export type PoolNode = KObject<Record<string, unknown>, NodeStatus & Record<string, unknown>>;

export type EnrollmentToken = KObject<{ pool: string; labels?: Record<string, string>; ttl?: string }, { expirationTime: string; usedTime?: string; node?: string; token?: string; installCommand?: string } & Record<string, unknown>>;

export type PoolActionSpec = {
  pool: string;
  action: ActPolicy["action"];
  targets?: { nodes?: string[]; objects?: string[] };
  reason: string;
  estimatedSavingsUSD?: string;
  mode: ActMode;
  decision?: "Pending" | "Approved" | "Rejected";
  expirationTime?: string;
}

export type PoolAction = KObject<PoolActionSpec, { phase?: string; reason?: string; executed?: boolean; revertible?: boolean; completionTime?: string; conditions?: Condition[] } & Record<string, unknown>>;

/** The clouds Nodus reaches with the customer's own API key and runs work in (ADR-130). */
export type KeyCloud = "Lambda" | "RunPod" | "DigitalOcean" | "Thunder" | "Nebius" | "Denvr" | "Jarvis" | "MassedCompute" | "Linode";

/** One GPU type of a Kubernetes namespace, at the customer's own hourly rate per GPU. */
export type KubernetesGPU = { type: string; hourlyUSD: string; nodeSelector?: Record<string, string> };

/** The grant to run work in the customer's own account and the credit Nodus may spend there. */
export type CloudExecute = { creditLimitUSD: string; period?: "Monthly" | "Total"; maxHourlyUSD?: string; burstToMarket?: BurstPolicy; priority?: number };

/** A machine a cloud account can start, at its lowest regional on-demand price, and the hours the credit left buys. */
export type CloudOffering = {
  instanceType: string;
  accelerator?: string;
  gpus?: number;
  hourlyUSD: string;
  creditHours: string;
  aboveHourlyCap?: boolean;
  spot?: boolean;
  regions: { region: string; hourlyUSD: string; inStock?: boolean }[];
};

/** A machine Nodus runs in a cloud account now, at the cloud's list rate frozen when it started. */
export type CloudRunningMachine = {
  name: string;
  instanceType: string;
  offering?: string;
  region: string;
  state: "Starting" | "Running" | "Idle" | "Stopping" | "Checking";
  hourlyUSD: string;
  sinceTime: string;
};

export type CloudExecuteStatus = {
  inUse?: boolean;
  machines?: number;
  creditUsedUSD?: string;
  creditRemainingUSD?: string;
  creditReservedUSD?: string;
  hourlyLimitUSD?: string;
  hourlyExposureUSD?: string;
  hourlyHeadroomUSD?: string;
  periodStartTime?: string;
  lastError?: { reason: string; message: string; time: string };
};

/** The credit left in a cloud account: reported by the cloud, stated by the customer, or a key account's limit. */
export type CloudCreditStatus = { remainingUSD: string; expireTime?: string; source: "Cloud" | "Stated" | "ExecuteLimit"; statedUSD?: string; statedTime?: string; asOfTime: string; message?: string };

/** A machine in the customer's own account that Nodus can no longer delete, as the cloud's console names it; a Kubernetes pod by its namespace. */
export type CloudMachine = { location: string; name: string };
/** One GPU quota: AWS and Azure count vCPUs of a GPU family per region, GCP counts GPUs per metric and region. */
export type CloudGPUQuota = { region: string; project?: string; family: string; limit: number; used?: number; unit: "GPUs" | "vCPUs"; requestUrl?: string };

export type CloudAccount = KObject<
  { provider: "AWS" | "GCP" | "Azure" | "Kubernetes" | KeyCloud; kubernetes?: { namespace: string; region?: string; gpus: KubernetesGPU[]; scaleUpWaitMinutes?: number }; aws?: { accountId: string; roleArn?: string; regions?: string[] }; gcp?: { projectIds: string[]; method?: string }; azure?: { tenantId: string; subscriptionId: string; regions?: string[] }; observe?: string[]; execute?: CloudExecute; credits?: { amountUSD?: string; expireTime?: string } },
  { setup?: { id: string; state: string; purpose?: string; message?: string }; capabilities?: { capability: string; state: string; reason: string; message?: string }[]; phase?: string; conditions?: Condition[]; handledRequests?: Record<string, string>; externalId?: string; inventory?: { instances: number; gpus?: Record<string, number>; runningGPUs?: Record<string, number>; regions?: string[]; gpuQuota?: CloudGPUQuota[] }; spend?: { monthToDateUSD: string; asOfTime: string; report?: CloudSpendReport }; lastSyncTime?: string; execute?: CloudExecuteStatus; credits?: CloudCreditStatus; machinesMayBeRunning?: CloudMachine[] } & Record<string, unknown>
>;

/** The clouds Nodus observes through a grant the customer creates, rather than reaching by their key. */
const OBSERVED_CLOUDS: readonly string[] = ["AWS", "GCP", "Azure"];

/** Whether Nodus reaches the account by the customer's key, so it runs work there rather than observing it. */
export function isKeyAccount(a: CloudAccount): boolean {
  return a.spec?.provider !== undefined && !OBSERVED_CLOUDS.includes(a.spec.provider);
}

/** Verification belongs to the current spec; execution readiness is a separate permission. */
export function cloudVerified(a: CloudAccount): boolean {
  return a.status?.conditions?.some((c) => c.type === "Verified" && c.status === "True" &&
    c.observedGeneration === (a.metadata.generation ?? 1)) ?? false;
}

/** A previously verified grant is being checked against changed settings, not awaiting a new grant. */
export function cloudVerificationPending(a: CloudAccount): boolean {
  return a.status?.conditions?.some((c) => c.type === "Verified" && c.status === "True" &&
    (c.observedGeneration ?? 0) < (a.metadata.generation ?? 1)) ?? false;
}

/** Permissions from an older account configuration must not appear ready. */
export function cloudExecutionCondition(a: CloudAccount): Condition | undefined {
  return a.status?.conditions?.find((c) => c.type === "ExecuteReady" && c.observedGeneration === (a.metadata.generation ?? 1));
}

export function cloudConnected(a: CloudAccount): boolean {
	const monitorReady = a.status?.capabilities?.some((c) => c.capability === "Connection" &&
		c.reason === "CustomerInventoryVerified" && c.state === "Ready") ?? false;
  const execution = cloudExecutionCondition(a);
  const grantVerified = execution?.status === "True" || (execution?.status === "False" &&
    ["NotAdmitted", "ProviderUnavailable", "CreditExhausted", "HourlyLimitReached"].includes(execution.reason ?? ""));
  return isKeyAccount(a) ? monitorReady || grantVerified : cloudVerified(a);
}

/** A key-cloud account proven only for read-only customer inventory, regardless of a later execute-spec edit. */
export function isMonitorAccount(a: CloudAccount): boolean {
	return isKeyAccount(a) && (a.status?.capabilities?.some((c) => c.capability === "Connection" &&
		c.reason === "CustomerInventoryVerified") ?? false);
}

/** Pending alone is ambiguous; a verification request or a changed verified account means checking. */
export function cloudConnectionPhase(a: CloudAccount): string {
  if (cloudConnected(a)) return "Ready";
  if (!isKeyAccount(a) && cloudVerificationPending(a)) return "Checking";
  const verified = a.status?.conditions?.find((c) => c.type === "Verified");
  if (verified?.observedGeneration === (a.metadata.generation ?? 1) && ["ApprovalPending", "ProjectSelectionRequired"].includes(verified.reason ?? "")) return "SetupRequired";
  const requested = a.metadata.annotations?.["nodus.dev/verify-requested-at"];
  if (requested && a.status?.handledRequests?.verify !== requested) return "Checking";
  if (!verified) return "SetupRequired";
  return "NeedsAttention";
}

/** The platform fees, stated next to the feature that incurs them (ui-spec §6.12). The pricebook is the source. */
export const ROUTE_FEE = "$0.02 per device-hour";
export const PREDICT_FEE = "$99.00 per pool per month, prorated by the second";

export const ACT_HINTS: Record<ActMode, string> = {
  Off: "No actions are proposed or run",
  Shadow: "Records what would run, runs nothing",
  Propose: "Each action waits for approval",
  Auto: "Runs within the policies",
};

export const OFFLINE_AFTER_MS = 10 * 60_000;

export function condition(conds: Condition[] | undefined, type: string): Condition | undefined {
  return conds?.find((c) => c.type === type);
}

/** The pool card status: Waiting for a host until a Node is Ready, then Ready or Failed. */
export function poolState(p: Pool): { word: string; tone: "neutral" | "success" | "danger" | "warning" } {
  const phase = p.status?.phase;
  if (phase === "Failed") return { word: "Failed", tone: "danger" };
  if (!p.status?.capacity?.readyNodes) return { word: "Waiting for a host", tone: "neutral" };
  if (condition(p.status.conditions, "CapacityAvailable")?.status === "False") return { word: "Full", tone: "warning" };
  return { word: "Ready", tone: "success" };
}

export function gpuTotals(p: Pool): { busy: number; total: number; byType: string } {
  const gpus = Object.entries(p.status?.capacity?.gpus ?? {});
  const total = gpus.reduce((n, [, d]) => n + d.total, 0);
  const free = gpus.reduce((n, [, d]) => n + d.free, 0);
  return { busy: total - free, total, byType: gpus.map(([t, d]) => `${d.total} × ${t.toUpperCase()}`).join(", ") };
}

export type NodeState = "Ready" | "Draining" | "Offline" | "Unenrolling" | "Pending";

/** The reasons a recommendation dismissal records (api/nodus/v1 PoolRecommendationDismissal). */
export type DismissReason = "Done" | "NotUseful" | "Other";

export const DISMISS_REASONS: { value: DismissReason; label: string }[] = [
  { value: "Done", label: "Already done outside Nodus" },
  { value: "NotUseful", label: "Not useful" },
  { value: "Other", label: "Other" },
];

/** The one annotation a client writes on a Node: present drains the host, absent undrains it. */
export const DRAIN_ANNOTATION = "nodus.dev/drain-requested-at";

/** Whether the host takes no new work by request; it stays drained while offline, so Undrain reads this, not the state. */
export function isDrained(n: PoolNode): boolean {
  return Boolean(n.status?.drainedTime || n.metadata.annotations?.[DRAIN_ANNOTATION]);
}

export function nodeState(n: PoolNode, now: Date): NodeState {
  if (n.metadata.deletionTimestamp) return "Unenrolling";
  const hb = n.status?.lastHeartbeatTime ? Date.parse(n.status.lastHeartbeatTime) : undefined;
  if (hb === undefined || !Number.isFinite(hb)) return "Pending";
  if (now.getTime() - hb >= OFFLINE_AFTER_MS) return "Offline";
  const ready = condition(n.status?.conditions, "Ready");
  if (!ready || ready.status === "Unknown" || ready.reason === "Registering") return "Pending";
  if (ready.status !== "True") return "Offline";
  if (isDrained(n) || condition(n.status?.conditions, "Draining")?.status === "True") return "Draining";
  return "Ready";
}

export function nodeGPUs(n: PoolNode): string {
  const gpus = n.status?.inventory?.gpus ?? [];
  if (!gpus.length) return "CPU only";
  return `${gpus.length} × ${gpus[0]!.type.toUpperCase()}`;
}

export function tokenState(t: EnrollmentToken, now: Date): "Unused" | "Used" | "Expired" {
  if (t.status?.usedTime) return "Used";
  if (t.status?.expirationTime && Date.parse(t.status.expirationTime) <= now.getTime()) return "Expired";
  return "Unused";
}

/** "expires in 23 h 58 m" for a live countdown. */
export function countdown(until: string | undefined, now: Date): string {
  if (!until) return "";
  const ms = Date.parse(until) - now.getTime();
  if (ms <= 0) return "expired";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return `expires in ${h} h ${m} m`;
}

export function awaitingApproval(a: PoolAction): boolean {
  return a.spec?.decision === "Pending" && a.status?.phase !== "Cancelled" && a.status?.phase !== "Succeeded";
}
